import { Link } from "@tanstack/react-router";
import { ArrowRight, MapPin, Phone, CheckCircle2 } from "lucide-react";

/**
 * Visually distinct Colorado Springs landing-style block — different from the
 * long-form SEO content section. Targets "Colorado Springs marketing
 * services", "local marketing Colorado Springs", "Colorado Springs SEO
 * company", "Colorado Springs marketing consultant" with a strong local CTA.
 */
export function ColoradoSpringsLandingBlock() {
  return (
    <section className="relative overflow-hidden bg-gradient-dark py-20 text-brand-cream sm:py-24">
      <div className="absolute inset-0 bg-gradient-radial-gold opacity-40" />
      <div className="relative mx-auto max-w-6xl px-5 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1.1fr,1fr] lg:items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold">
              <MapPin className="h-3.5 w-3.5" /> Based in Colorado Springs
            </span>
            <h2 className="mt-5 text-4xl font-black leading-[1.05] text-white sm:text-5xl">
              The Colorado Springs marketing team{" "}
              <span className="text-gradient-gold">your competitors don't want you to find</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-brand-cream/85">
              While Denver agencies pitch you over Zoom and national firms
              hand your account to a junior, we're 15 minutes from your front
              door — answering the phone, walking your job sites, and ranking
              Colorado Springs businesses in the Map Pack since 2006.
            </p>

            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {[
                "Local Colorado Springs SEO & Map Pack specialists",
                "In-person strategy meetings on the north side",
                "Neighborhood-level content (Briargate to Broadmoor)",
                "Owner-led — you talk to Chris, not a sales rep",
                "No 12-month contracts, ever",
                "200+ Colorado businesses ranked since 2006",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-brand-cream/90">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-gold" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
              >
                Book my free Colorado Springs audit
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+17194193935"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-brand-gold/40 bg-brand-black/40 px-7 py-4 text-base font-bold text-brand-cream hover:bg-brand-gold/10"
              >
                <Phone className="h-5 w-5 text-brand-gold" />
                (719) 419-3935
              </a>
            </div>
            <p className="mt-4 text-xs text-brand-cream/60">
              Free 30-minute call. No sales pitch, no credit card. We'll audit
              your site, GBP, and top 3 Colorado Springs competitors.
            </p>
          </div>

          <div className="rounded-2xl border border-brand-gold/30 bg-brand-black/50 p-7 backdrop-blur">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold">
              Colorado Springs Marketing Services
            </p>
            <h3 className="mt-3 text-2xl font-bold text-white">
              Built for businesses on the Front Range
            </h3>
            <ul className="mt-5 divide-y divide-brand-gold/15">
              {[
                { to: "/colorado-springs-seo", label: "Colorado Springs SEO" },
                { to: "/colorado-springs-web-design", label: "Web design in Colorado Springs" },
                { to: "/colorado-springs-google-maps", label: "Google Maps & local pack" },
                { to: "/google-ads-ppc", label: "Google Ads & PPC management" },
                { to: "/geo-ai", label: "GEO / AI search optimization" },
                { to: "/colorado-springs", label: "Full Colorado Springs marketing hub" },
              ].map((s) => (
                <li key={s.to}>
                  <Link
                    to={s.to}
                    className="group flex items-center justify-between gap-3 py-3 text-brand-cream/90 hover:text-brand-gold"
                  >
                    <span className="text-sm font-semibold">{s.label}</span>
                    <ArrowRight className="h-4 w-4 text-brand-gold/60 transition-transform group-hover:translate-x-1 group-hover:text-brand-gold" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
